import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDebounce } from "use-debounce";
import { LayoutGrid, Search, ChevronDown, X } from "lucide-react";

import { taskboard } from "../../services/taskboardService";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { ROLES } from "../../config/roles";
import PlayerFolderCard from "../../components/agency/PlayerFolderCard";
import CreateFolderModal from "../../components/agency/CreateFolderModal";
import AgencyTaskboardMobileHeader from "../../components/agency/responsive/AgencyTaskboardMobileHeader";
import { lastActivityMs, hasStaleWaiting, staleWaitingDays } from "../../utils/taskboardActivity";
import { entryBoardStatus, STATUS } from "../../constants/taskboardStatus";

// ──────────────────────────────────────────────────────────────────────
// AgencyTaskboard — folder grid, one card per (agency, player).
// Redesign: heartbeat-first cards, attention state for stale Waiting,
// sort by last activity (default) or player name, search by player /
// position / club, and filters for managing agent + "has open items".
//
// Per-folder signals (last activity, has-open, stale) are derived from
// the embedded entries when the API includes them (mock always does;
// the real API adds folder-level hints per the contract). When neither
// is present the card degrades gracefully to folder.last_activity_at.
// ──────────────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: "activity", label: "Last activity" },
  { value: "name", label: "Player name" },
];

// Derive the signals a card needs from whatever the folder carries.
function deriveSignals(folder, now) {
  const entries = folder.entries || [];
  const ms = lastActivityMs(folder, entries, folder.links || []);
  // "has open items": any non-Done entry. Prefer a folder-level hint;
  // fall back to embedded entries (mock). When neither is available
  // (real list carries no per-entry status) leave it null = unknown, so
  // the client trusts the server-side has_open filter instead of
  // guessing from entries_count.
  let hasOpen = null;
  if (typeof folder.has_open_items === "boolean") {
    hasOpen = folder.has_open_items;
  } else if (entries.length) {
    hasOpen = entries.some((e) => entryBoardStatus(e) !== STATUS.DONE);
  }
  const isStale =
    typeof folder.has_stale_waiting === "boolean"
      ? folder.has_stale_waiting
      : hasStaleWaiting(entries, 7, now);
  const staleDays = typeof folder.stale_waiting_days === "number" ? folder.stale_waiting_days : staleWaitingDays(entries, 7, now);
  return { ms, hasOpen, isStale, staleDays };
}

const SkeletonCard = () => (
  <div className="bg-white border border-[#E4E9F0] rounded-[14px] p-4 flex flex-col gap-3 animate-pulse">
    <div className="flex gap-3 items-center">
      <div className="w-11 h-11 rounded-full bg-[#EEF1F6]" />
      <div className="flex flex-col gap-2 flex-1">
        <div className="h-3.5 w-2/3 bg-[#EEF1F6] rounded" />
        <div className="h-3 w-1/2 bg-[#F2F4F7] rounded" />
      </div>
    </div>
    <div className="h-6 w-28 bg-[#F2F4F7] rounded-full" />
    <div className="border-t border-[#F2F4F7] pt-2.5 flex gap-2">
      <div className="h-4 w-24 bg-[#F2F4F7] rounded" />
      <div className="ml-auto h-4 w-16 bg-[#F2F4F7] rounded-full" />
    </div>
  </div>
);

const AgencyTaskboard = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const now = Date.now();

  const { currentRole } = useSelector((state) => state.auth) || {};
  const isAgentView = currentRole === ROLES.Agent;

  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [openOnly, setOpenOnly] = useState(false);
  const [sortBy, setSortBy] = useState("activity");
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const [debouncedSearch] = useDebounce(search, 250);

  // Server does the authoritative filtering/sorting (it alone sees every
  // entry's status). We still refine client-side for instant feedback,
  // guarded so we never override the server. agent_account_id is resolved
  // from the loaded folders (the toolbar shows agent names).
  const agentAccountId = useMemo(() => {
    if (agentFilter === "all") return undefined;
    const f = folders.find((x) => x.current_agent?.name === agentFilter);
    return f?.current_agent?.account_id;
  }, [folders, agentFilter]);

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { sort: sortBy === "name" ? "name" : "last_activity" };
    if (openOnly) params.has_open = true;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (agentAccountId) params.agent_account_id = agentAccountId;
    const response = await taskboard.getFolders(params);
    if (!response.success) {
      setError(response.error || "Could not load folders");
      setFolders([]);
    } else {
      setFolders(response.data || []);
    }
    setLoading(false);
  }, [sortBy, openOnly, debouncedSearch, agentAccountId]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Signals per folder id (memoized so sort/filter don't recompute per row).
  const signals = useMemo(() => {
    const map = {};
    for (const f of folders) map[f.id] = deriveSignals(f, now);
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders]);

  const agentOptions = useMemo(() => {
    const names = new Set();
    folders.forEach((f) => f.current_agent?.name && names.add(f.current_agent.name));
    return ["all", ...Array.from(names).sort()];
  }, [folders]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    let list = folders.filter((f) => {
      if (needle) {
        const hay = [
          f.player?.name,
          f.player?.position,
          f.player?.current_club?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (agentFilter !== "all" && f.current_agent?.name !== agentFilter) return false;
      // Only drop when we can prove there are no open items; when unknown
      // (null) trust the server's has_open filter.
      if (openOnly && signals[f.id]?.hasOpen === false) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sortBy === "name") {
        return (a.player?.name || "").localeCompare(b.player?.name || "");
      }
      return (signals[b.id]?.ms || 0) - (signals[a.id]?.ms || 0);
    });
    return list;
  }, [folders, search, agentFilter, openOnly, sortBy, signals]);

  const activeFilters =
    (search.trim() ? 1 : 0) + (agentFilter !== "all" ? 1 : 0) + (openOnly ? 1 : 0);
  const clearFilters = () => {
    setSearch("");
    setAgentFilter("all");
    setOpenOnly(false);
  };

  const btn =
    "flex items-center gap-1.5 bg-white border border-[#E4E9F0] rounded-[9px] px-3 py-2 text-[13px] font-medium text-[#344054] hover:bg-gray-50";

  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      {isMobile ? (
        <AgencyTaskboardMobileHeader onAddEntry={() => setShowCreate(true)} />
      ) : (
        <div className="px-8 pt-7 flex items-center gap-3">
          <h1 className="text-[24px] font-bold tracking-[-.01em] text-[#101828]">
            {isAgentView ? "My Taskboard" : "Taskboard"}
          </h1>
          <span className="bg-[#EAF2FF] text-[#1866CC] text-[12px] font-semibold px-2.5 py-0.5 rounded-full">
            {folders.length} {folders.length === 1 ? "folder" : "folders"}
          </span>
          <button
            onClick={() => setShowCreate(true)}
            className="ml-auto flex items-center gap-1.5 bg-[#2283FF] text-white rounded-[9px] px-4 py-2.5 text-[13.5px] font-semibold shadow-sm hover:bg-[#1866CC]"
          >
            <span className="text-[16px] leading-none">+</span> Create folder
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className={`${isMobile ? "px-4 pt-4" : "px-8 pt-4"} pb-3 flex flex-wrap items-center gap-2.5`}>
        <div className="flex items-center gap-2 bg-white border border-[#E4E9F0] rounded-[9px] px-3 py-2 w-full sm:w-[300px]">
          <Search className="w-[15px] h-[15px] text-[#98A2B3] flex-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search player, position, club…"
            className="w-full text-[13.5px] outline-none placeholder:text-[#98A2B3]"
          />
        </div>

        {/* Agent filter */}
        {!isAgentView && (
          <div className="relative">
            <button className={btn} onClick={() => { setAgentMenuOpen((o) => !o); setSortMenuOpen(false); }}>
              Agent: {agentFilter === "all" ? "All" : agentFilter}
              <ChevronDown className="w-3 h-3 text-[#667085]" />
            </button>
            {agentMenuOpen && (
              <div className="absolute z-20 mt-1 w-56 bg-white border border-[#E4E9F0] rounded-lg shadow-lg py-1 max-h-64 overflow-auto">
                {agentOptions.map((a) => (
                  <button
                    key={a}
                    onClick={() => { setAgentFilter(a); setAgentMenuOpen(false); }}
                    className={`block w-full text-left px-3 py-2 text-[13px] hover:bg-[#F8FAFC] ${
                      agentFilter === a ? "text-[#1866CC] font-semibold" : "text-[#344054]"
                    }`}
                  >
                    {a === "all" ? "All agents" : a}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Has open items */}
        <button
          onClick={() => setOpenOnly((o) => !o)}
          aria-pressed={openOnly}
          className={`flex items-center gap-1.5 rounded-[9px] px-3 py-2 text-[13px] font-medium border ${
            openOnly
              ? "bg-[#EAF2FF] border-[#B8D4FF] text-[#1866CC]"
              : "bg-white border-[#E4E9F0] text-[#344054] hover:bg-gray-50"
          }`}
        >
          Has open items
        </button>

        {/* Sort */}
        <div className="relative ml-auto">
          <div className="flex items-center gap-2 text-[13px] text-[#667085]">
            <span className="hidden sm:inline">Sort</span>
            <button className={btn} onClick={() => { setSortMenuOpen((o) => !o); setAgentMenuOpen(false); }}>
              {SORT_OPTIONS.find((s) => s.value === sortBy)?.label}
              <ChevronDown className="w-3 h-3 text-[#667085]" />
            </button>
          </div>
          {sortMenuOpen && (
            <div className="absolute right-0 z-20 mt-1 w-44 bg-white border border-[#E4E9F0] rounded-lg shadow-lg py-1">
              {SORT_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => { setSortBy(s.value); setSortMenuOpen(false); }}
                  className={`block w-full text-left px-3 py-2 text-[13px] hover:bg-[#F8FAFC] ${
                    sortBy === s.value ? "text-[#1866CC] font-semibold" : "text-[#344054]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilters > 0 && (
        <div className={`${isMobile ? "px-4" : "px-8"} pb-3 flex items-center gap-2 flex-wrap`}>
          <span className="text-[13px] text-[#667085]">
            {visible.length} {visible.length === 1 ? "result" : "results"}
          </span>
          {search.trim() && (
            <FilterChip label={`"${search.trim()}"`} onClear={() => setSearch("")} />
          )}
          {agentFilter !== "all" && (
            <FilterChip label={`Agent: ${agentFilter}`} onClear={() => setAgentFilter("all")} />
          )}
          {openOnly && <FilterChip label="Has open items" onClear={() => setOpenOnly(false)} />}
          <button onClick={clearFilters} className="text-[13px] text-[#1866CC] font-medium hover:underline">
            Clear all
          </button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${isMobile ? "px-4 pb-10" : "px-8 pb-10"}`}>
          {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : error ? (
        <div className="text-center py-16 text-[#B91C1C]">{error}</div>
      ) : visible.length === 0 ? (
        <EmptyState
          filtered={activeFilters > 0}
          onCreate={() => setShowCreate(true)}
          onClear={clearFilters}
        />
      ) : (
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${isMobile ? "px-4 pb-10" : "px-8 pb-10"}`}>
          {visible.map((folder) => (
            <PlayerFolderCard
              key={folder.id}
              folder={folder}
              lastActivityMs={signals[folder.id]?.ms || 0}
              isStale={signals[folder.id]?.isStale || false}
              staleDays={signals[folder.id]?.staleDays || 0}
              now={now}
            />
          ))}
        </div>
      )}

      <CreateFolderModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onSuccess={(folder) => {
          fetchFolders();
          if (folder && folder.id) navigate(`/agency/folders/${folder.id}`);
        }}
      />
    </div>
  );
};

const FilterChip = ({ label, onClear }) => (
  <span className="inline-flex items-center gap-1.5 bg-white border border-[#E4E9F0] rounded-full pl-3 pr-1.5 py-1 text-[12.5px] text-[#344054]">
    {label}
    <button onClick={onClear} className="w-4 h-4 rounded-full hover:bg-[#F2F4F7] flex items-center justify-center" aria-label={`Clear ${label}`}>
      <X className="w-3 h-3 text-[#98A2B3]" />
    </button>
  </span>
);

const EmptyState = ({ filtered, onCreate, onClear }) => (
  <div className="px-8 py-20 flex flex-col items-center justify-center text-center">
    <div className="w-14 h-14 rounded-full bg-[#EAF2FF] flex items-center justify-center mb-4">
      <LayoutGrid className="w-6 h-6 text-[#2283FF]" />
    </div>
    {filtered ? (
      <>
        <p className="text-[15px] font-semibold text-[#101828] mb-1">No folders match your filters</p>
        <p className="text-[13.5px] text-[#667085] mb-4">Try a different search or clear the filters.</p>
        <button onClick={onClear} className="text-[13.5px] text-[#1866CC] font-semibold hover:underline">
          Clear all filters
        </button>
      </>
    ) : (
      <>
        <p className="text-[15px] font-semibold text-[#101828] mb-1">No folders yet</p>
        <p className="text-[13.5px] text-[#667085] mb-4 max-w-sm">
          Create a folder to start tracking a player: log calls, meetings and
          offers, and attach scout reports or videos.
        </p>
        <button
          onClick={onCreate}
          className="flex items-center gap-1.5 bg-[#2283FF] text-white rounded-[9px] px-4 py-2.5 text-[13.5px] font-semibold shadow-sm hover:bg-[#1866CC]"
        >
          <span className="text-[16px] leading-none">+</span> Create folder
        </button>
      </>
    )}
  </div>
);

export default AgencyTaskboard;
